import { c as _c } from "react/compiler-runtime";
import { DateFilterChip } from "@components/common/faro-table/faro-table-filter/date-filter/date-filter-chip";
import { DropdownFilterChip } from "@components/common/faro-table/faro-table-filter/dropdown-filter-chip";
import { PROJECTS_ACCESS_LEVEL_FILTER_COLUMN, PROJECTS_CREATED_FILTER_COLUMN, PROJECTS_FILTER_COLUMNS, PROJECTS_SINGLE_FILTER_COLUMNS, PROJECTS_SIZE_FILTER_COLUMN } from "@components/projects/projects-filters/projects-filter-constants";
import { getProjectsAccessLevelFilterOptions, getProjectsCreatedFilterPresets, getProjectsFilterOptions } from "@components/projects/projects-filters/projects-filter-options";
import { getProjectsSizeFilterPresets } from "@components/projects/projects-filters/projects-size-filter-utils";
import { SizeFilterChip } from "@components/projects/projects-filters/size-filter/size-filter-chip";
import { PROJECTS_TABLE_COLUMN_LABELS } from "@components/table/projects/projects-table-constants";
import { useProjectsTableContext } from "@components/table/projects/use-projects-table-context";
import { doesColumnPassAnyFilter } from "@hooks/table-filters/use-apply-filter-utils";
import { useFilterQueryParams } from "@hooks/table-filters/use-filter-query-params";
import { TableType } from "@utils/track-event/track-event-list";
import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

/** The filters the user applied to a column, which the column offers */

/**
 * Applies the access level, creation date and size filters to the projects and returns the chips
 * to change them. The API cannot filter the projects by them, so it only works on projects that
 * were all fetched at once.
 */
export function useProjectsFilters(t0) {
  const $ = _c(23);
  const {
    projectArchivingState
  } = t0;
  const {
    getFilterIdsForColumn
  } = useFilterQueryParams();
  let t1;
  if ($[0] !== projectArchivingState) {
    t1 = {
      projectArchivingState
    };
    $[0] = projectArchivingState;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const contextType = useProjectsTableContext(t1);
  const tableType = contextType === "active projects" ? TableType.activeProjects : TableType.archiveProjects;
  let t2;
  if ($[2] !== getFilterIdsForColumn) {
    t2 = column => {
      const filterIds = getFilterIdsForColumn(column);
      return {
        column,
        filterIds,
        activeFilterIds: getKnownFilterIds(column, filterIds)
      };
    };
    $[2] = getFilterIdsForColumn;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const filtersByColumn = PROJECTS_FILTER_COLUMNS.map(t2);
  const isAnyFilterApplied = filtersByColumn.some(_temp);
  const hasUnknownFilterIds = filtersByColumn.some(_temp2);
  const [, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!hasUnknownFilterIds) {
      return;
    }
    setSearchParams(_temp3, {
      replace: true
    });
  }, [hasUnknownFilterIds, setSearchParams]);
  const activeFiltersKey = JSON.stringify(filtersByColumn.filter(_temp4).map(_temp5));
  let t3;
  if ($[4] !== activeFiltersKey) {
    t3 = projects => {
      const activeFilters = JSON.parse(activeFiltersKey);
      if (activeFilters.length === 0) {
        return projects;
      }
      const columnFilters = activeFilters.map(_temp6);
      return projects.filter(project => columnFilters.every(columnFilter => doesColumnPassAnyFilter({
        entity: project,
        ...columnFilter
      })));
    };
    $[4] = activeFiltersKey;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const filterProjects = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = getProjectsAccessLevelFilterOptions();
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== tableType) {
    t5 = <DropdownFilterChip key={PROJECTS_ACCESS_LEVEL_FILTER_COLUMN} label={PROJECTS_TABLE_COLUMN_LABELS.accessLevel} filteredColumn={PROJECTS_ACCESS_LEVEL_FILTER_COLUMN} items={t4} shouldShowSearchBox={false} shouldShowCheckbox={true} shouldAllowLabelTranslation={true} tableType={tableType} />;
    $[7] = tableType;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = getProjectsCreatedFilterPresets();
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== tableType) {
    t7 = <DateFilterChip key={PROJECTS_CREATED_FILTER_COLUMN} label={PROJECTS_TABLE_COLUMN_LABELS.created} filteredColumn={PROJECTS_CREATED_FILTER_COLUMN} presets={t6} tableType={tableType} />;
    $[10] = tableType;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = getProjectsSizeFilterPresets();
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== tableType) {
    t9 = <SizeFilterChip key={PROJECTS_SIZE_FILTER_COLUMN} label={PROJECTS_TABLE_COLUMN_LABELS.size} filteredColumn={PROJECTS_SIZE_FILTER_COLUMN} presets={t8} tableType={tableType} />;
    $[13] = tableType;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t5 || $[16] !== t7 || $[17] !== t9) {
    t10 = {
      [PROJECTS_ACCESS_LEVEL_FILTER_COLUMN]: t5,
      [PROJECTS_CREATED_FILTER_COLUMN]: t7,
      [PROJECTS_SIZE_FILTER_COLUMN]: t9
    };
    $[15] = t5;
    $[16] = t7;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const filterChips = t10;
  let t11;
  if ($[19] !== filterChips || $[20] !== filterProjects || $[21] !== isAnyFilterApplied) {
    t11 = {
      isAnyFilterApplied,
      filterProjects,
      filterChips
    };
    $[19] = filterChips;
    $[20] = filterProjects;
    $[21] = isAnyFilterApplied;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
}

/**
 * Returns the IDs that are filters the column offers. A column that applies only one filter keeps
 * the first of them, as its chip shows only that one.
 */
function _temp6(t0) {
  const [column_2, activeFilterIds_3] = t0;
  return {
    activeFilterIds: activeFilterIds_3,
    allPossibleFiltersForColumn: getProjectsFilterOptions(column_2, activeFilterIds_3)
  };
}
function _temp5(t0) {
  const {
    column: column_1,
    activeFilterIds: activeFilterIds_2
  } = t0;
  return [column_1, activeFilterIds_2];
}
function _temp4(t0) {
  const {
    activeFilterIds: activeFilterIds_1
  } = t0;
  return activeFilterIds_1.length > 0;
}
function _temp3(searchParams) {
  PROJECTS_FILTER_COLUMNS.forEach(column_0 => {
    const filterIds_1 = searchParams.getAll(column_0);
    const knownFilterIds = getKnownFilterIds(column_0, filterIds_1);
    if (knownFilterIds.length === filterIds_1.length) {
      return;
    }
    searchParams.delete(column_0);
    knownFilterIds.forEach(filterId => searchParams.append(column_0, filterId));
  });
  return searchParams;
}
function _temp2(t0) {
  const {
    filterIds: filterIds_0,
    activeFilterIds: activeFilterIds_0
  } = t0;
  return activeFilterIds_0.length < filterIds_0.length;
}
function _temp(t0) {
  const {
    activeFilterIds
  } = t0;
  return activeFilterIds.length > 0;
}
function getKnownFilterIds(column, filterIds) {
  const filters = getProjectsFilterOptions(column, filterIds);
  const knownFilterIds = filterIds.filter(filterId => filterId in filters);
  return PROJECTS_SINGLE_FILTER_COLUMNS.includes(column) ? knownFilterIds.slice(0, 1) : knownFilterIds;
}